import "@tento-ui/brand/header/marketing-header";
import "../marketing/lona-marketing-footer";
import "@tento-ui/brand/brand-app/brand-app";
import "./brand-variant-picker";

import SITE_NAV from "../../routes/nav.json" with { type: "json" };
import { SiteNav } from "@tento-ui/brand/@generated/models";
import type { Brand } from "@tento-ui/brand/brand";
import { BrandApp } from "@tento-ui/brand/brand-app/brand-app";
import {
  BrandVariantPicker,
  type BrandVariantPickerProps,
} from "./brand-variant-picker";
import { $ } from "@tento-ui/dom-selectors";
import { DomUtils } from "@tento-ui/dom";
import { DarkMode } from "@tento-ui/dark-mode";
import { Hotkeys } from "@tento-ui/hotkeys";
import { Router } from "@tento-ui/router";
import {
  type BrandVariant,
  BRAND_VARIANTS,
  allVariants,
  isBrandVariant,
  resolvePageEnginePreset,
  resolveVariant,
  showVariantPicker,
  switchVariant,
} from "./brand-variants";
import { BrandTokens } from "@tento-ui/brand/tokens/index";
import { BlogIndexController } from "./blog-index-controller";
import { BrandRouter } from "./brand-router";
import { registerBrandDecorators } from "./register-decorators";
import { navConfigFromSiteNav } from "./landing-nav";
import type { BlogVariantConfig } from "../blog/blog-variant";

const DEFAULT_BLOG_VARIANT: BrandVariant = "loaf";
const BLOG_NAV_CONFIG = navConfigFromSiteNav(
  SiteNav.parse(SITE_NAV),
  "root-host",
);

type LonaBrandAppConfig = {
  brandDS: BrandApp.Config["ds"];
  blogStyle: BrandApp.Config["pageStyle"];
  landing: Brand.LandingPage["landing"]["sections"];
  router: BlogVariantConfig;
};

export class LonaBrandApp {
  private variant: BrandVariant;
  private config: LonaBrandAppConfig;
  private $app: BrandApp;
  private indexController: Option<BlogIndexController> = null;
  private router: BrandRouter;

  constructor() {
    this.variant = resolveVariant();

    const brandConfig = BRAND_VARIANTS[this.variant];
    const fallback = BRAND_VARIANTS[DEFAULT_BLOG_VARIANT];

    const blogFallback = fallback.blogStyle ?? BrandTokens.Blog.ui;
    const fallbackBlog = fallback.blog;
    const fallbackBlogContent = fallback.blogContent;
    if (fallbackBlog == null || fallbackBlogContent == null) {
      throw new Error("Missing default blog variant config");
    }
    this.config = {
      brandDS: brandConfig.brandDS,
      blogStyle: brandConfig.blogStyle ?? blogFallback,
      landing: brandConfig.landing.sections,
      router: {
        chrome: brandConfig.chrome,
        blogContent: brandConfig.blogContent ?? fallbackBlogContent,
        routes: brandConfig.blog ?? fallbackBlog,
      },
    };
    this.$app = $<BrandApp>("app");
    this.router = new BrandRouter({
      context: {
        blog: this.config.router,
        landing: this.config.landing,
        onPostNavigate: (slug) => Router.navigateTo(`/${slug}`),
        onBlogBack: () => Router.navigateTo("/"),
      },
      initialPageModel: null,
      fallback: () => this.fallbackIndexElement(),
      resolvePreset: resolvePageEnginePreset,
    });
  }

  async init() {
    DarkMode.toggle(false, false);

    registerBrandDecorators();

    Hotkeys.init([
      ["v", () => {
        showVariantPicker.toggle();
        BrandVariantPicker.showPersistent(this.variantPickerProps());
      }],
    ]);

    this.router.register((resolve) => this.renderRoute(resolve), [
      { pattern: "/", path: () => "/" },
      { pattern: "/{slug}", path: ([slug]) => `/${slug}` },
    ]);
    await this.renderRoute(this.router.initialResolve(this.pathFromUrl()));

    if (showVariantPicker.value) {
      BrandVariantPicker.showPersistent(this.variantPickerProps());
    }

    await DomUtils.hydrateAndReveal();
  }

  private async renderRoute(resolve: BrandApp.Config["resolve"]) {
    await this.$app.render({
      ds: this.config.brandDS,
      pageStyle: this.config.blogStyle,
      navConfig: BLOG_NAV_CONFIG,
      resolve,
    });
  }

  private pathFromUrl(): string {
    const path = window.location.pathname;
    if (path === "") return "/";
    return path;
  }

  private fallbackIndexElement(): HTMLElement {
    if (!this.indexController) {
      this.indexController = new BlogIndexController(
        this.config.router,
        (slug) => Router.navigateTo(`/${slug}`),
        null,
      );
    }
    return this.indexController.element;
  }

  private variantPickerProps(): BrandVariantPickerProps {
    return {
      activeVariant: this.variant,
      variants: allVariants().map((variant) => ({
        id: variant,
        label: variant,
        detail: null,
      })),
      onSelect: (variant) => {
        if (isBrandVariant(variant)) switchVariant(variant);
      },
    };
  }
}
